import React, { useState, useRef, useEffect } from 'react';
import { User, Settings, LogOut, Shield, ChevronDown, Bookmark, Zap } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const UserDropdown = () => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    setIsOpen(false);
    await logout();
  };

  const getInitials = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`.toUpperCase();
    }
    return user?.username?.charAt(0)?.toUpperCase() || 'U';
  };

  const getDisplayName = (user) => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name} ${user.last_name}`;
    }
    return user?.username || 'User';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger - Mobile optimized */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2 px-2 md:px-3 py-2 rounded-lg hover:bg-videbits-50 transition-colors min-h-[44px]"
      >
        {/* Avatar */}
        <div className="w-8 h-8 bg-videbits-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
          {getInitials(user)}
        </div>
        
        {/* User Info */}
        <div className="hidden md:flex flex-col items-start">
          <span className="text-sm font-medium text-gray-900">
            {getDisplayName(user)}
          </span>
          {isAdmin && (
            <span className="text-xs text-videbits-600 font-medium">Admin</span>
          )}
        </div>
        
        {/* Dropdown Arrow */}
        <ChevronDown className={`w-4 h-4 text-videbits-500 transition-transform ${
          isOpen ? 'rotate-180' : ''
        }`} />
      </button>

      {/* Dropdown Menu - Mobile optimized */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 md:w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 max-w-[calc(100vw-2rem)]">
          {/* User Info Section */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-videbits-600 rounded-full flex items-center justify-center text-white font-medium">
                {getInitials(user)}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {getDisplayName(user)}
                </p>
                <p className="text-xs text-gray-500">@{user?.username}</p>
                <p className="text-xs text-gray-500">{user?.email}</p>
                {isAdmin && (
                  <div className="flex items-center space-x-1 mt-1">
                    <Shield className="w-3 h-3 text-videbits-600" />
                    <span className="text-xs text-videbits-600 font-medium">Administrator</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* Profile */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/profile');
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-videbits-700 hover:bg-videbits-50 transition-colors min-h-[44px]"
            >
              <User className="w-4 h-4 text-videbits-500" />
              <span>Profile</span>
            </button>

            {/* Watchlist */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/watchlist');
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-videbits-700 hover:bg-videbits-50 transition-colors min-h-[44px]"
            >
              <Bookmark className="w-4 h-4 text-videbits-500" />
              <span>Watchlist</span>
            </button>

            {/* Submit an Insider tip */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/insider/submit');
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-videbits-700 hover:bg-videbits-50 transition-colors min-h-[44px]"
            >
              <Zap className="w-4 h-4 text-videbits-600" />
              <span>Submit a tip</span>
            </button>

            {/* Admin Dashboard (admin only) */}
            {isAdmin && (
              <button
                onClick={() => {
                  setIsOpen(false);
                  navigate('/admin');
                }}
                className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-videbits-700 hover:bg-videbits-50 transition-colors min-h-[44px]"
              >
                <Shield className="w-4 h-4 text-videbits-600" />
                <span>Admin Dashboard</span>
              </button>
            )}

            {/* Settings */}
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/settings');
              }}
              className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-videbits-700 hover:bg-videbits-50 transition-colors min-h-[44px]"
            >
              <Settings className="w-4 h-4 text-videbits-500" />
              <span>Settings</span>
            </button>
          </div>

          {/* Logout */}
          <div className="border-t border-gray-100 py-1">
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50 transition-colors min-h-[44px]"
            >
              <LogOut className="w-4 h-4" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdown;
